import { Toggle, TooltipV2 } from '@cimplico/components';
import { Controller, useFormContext } from 'react-hook-form';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { SubscriptionTier } from '../../../types/firm';
import UpgradeHint from '../UpgradeHint';
import { WorkpaperRoleEnum } from '../../../types/workpaper';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import { useHasFeatureAccess } from '../../../hooks/use-has-feature-access';
import { useFirmContext } from '../../../providers/FirmProvider';

type SecuritySectionProps = {
    isDisabled: boolean;
};

function SecuritySection({ isDisabled }: SecuritySectionProps): JSX.Element {
    const { control } = useFormContext();
    const { role: workpaperRole } = useWorkpaperContext();
    const { isAdmin } = useIsFirmRole();
    const { subscriptionVersion } = useFirmContext();
    const featureAccess = { canTogglePrivate: useHasFeatureAccess('workpaper-toggle-private:write') };

    const canChangePrivateAccess = isAdmin || workpaperRole === WorkpaperRoleEnum.APPROVER;
    const canChangePreparer = isAdmin || [WorkpaperRoleEnum.APPROVER, WorkpaperRoleEnum.REVIEWER].includes(workpaperRole);

    return (
        <div className="flex flex-col gap-2">
            <h3 className="font-medium text-gray-800 text-lg">Security</h3>
            <p className="block text-sm font-medium text-gray-700 mb-1">Access Control</p>
            <Controller
                name="private"
                control={control}
                render={({ field: { onChange, value } }): JSX.Element => {
                    return (
                        <div className="inline-flex">
                            <TooltipV2 message="This requires an admin or approver role." disabled={canChangePrivateAccess || isDisabled}>
                                <Toggle
                                    onToggle={onChange}
                                    label="Private"
                                    defaultEnabled={value}
                                    isDisabled={!featureAccess.canTogglePrivate || isDisabled || !canChangePrivateAccess}
                                />
                            </TooltipV2>
                        </div>
                    );
                }}
            />
            <label className="mt-2 text-sm text-gray-500">* Only assignees will have access to this workpaper.</label>
            {subscriptionVersion === 1 && <UpgradeHint plan={SubscriptionTier.ESSENTIAL} message="make workpapers private" />}
            <div className="mt-4 mb-2 w-fit">
                <TooltipV2 message="This requires an admin or approver/reviewer role." disabled={canChangePreparer || isDisabled}>
                    <Controller
                        name="permissions.preparer.canPostJournal"
                        control={control}
                        render={({ field: { onChange, value } }): JSX.Element => {
                            return (
                                <Toggle
                                    onToggle={onChange}
                                    label="Allow preparers to post journals"
                                    defaultEnabled={value}
                                    isDisabled={!canChangePreparer || isDisabled}
                                />
                            );
                        }}
                    />
                </TooltipV2>
            </div>
        </div>
    );
}

export default SecuritySection;
